import React, { useState, useEffect, useCallback } from 'react';
import { accountingAPI, companyProfileAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Plus, Search, BookOpenCheck, FileDown, Edit, ChevronDown, ChevronRight, Save, X, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const VOUCHER_TYPES = [
  { value: 'sales', label: 'Sales' }, { value: 'purchase', label: 'Purchase' },
  { value: 'journal', label: 'Journal' }, { value: 'receipt', label: 'Receipt' },
  { value: 'payment', label: 'Payment' }, { value: 'contra', label: 'Contra' },
  { value: 'credit_note', label: 'Cr Note' }, { value: 'debit_note', label: 'Dr Note' },
  { value: 'expense', label: 'Expense' },
];

const emptyLine = { account_code: '', debit: '', credit: '', narration: '' };

export default function LedgerPage() {
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [search, setSearch] = useState('');
  const [ledger, setLedger] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', page: 1 });
  // Inline entry form
  const [entryDialog, setEntryDialog] = useState(false);
  const [entryForm, setEntryForm] = useState({
    date: new Date().toISOString().split('T')[0], voucher_type: 'journal',
    narration: '', party_name: '', lines: [{ ...emptyLine }, { ...emptyLine }]
  });
  const [saving, setSaving] = useState(false);
  // Edit mode
  const [editDialog, setEditDialog] = useState({ open: false, entry: null });
  const [editForm, setEditForm] = useState(null);
  // Detail expand
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    Promise.all([
      accountingAPI.chartOfAccounts({}),
      companyProfileAPI.get()
    ]).then(([accs, comp]) => {
      const leaf = accs.data.filter(a => !a.is_group);
      setAccounts(leaf);
      setAllAccounts(accs.data);
      setCompany(comp.data);
    }).catch(() => {});
  }, []);

  const loadLedger = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params = { page: filters.page, limit: 200 };
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await accountingAPI.accountLedger(selectedAccount, params);
      setLedger(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [selectedAccount, filters]);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const filtered = accounts.filter(a =>
    a.account_name.toLowerCase().includes(search.toLowerCase()) || a.account_code.includes(search)
  );

  // Add Entry inline
  const updateLine = (idx, field, val) => setEntryForm(f => {
    const lines = [...f.lines]; lines[idx] = { ...lines[idx], [field]: val }; return { ...f, lines };
  });
  const addLine = () => setEntryForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const removeLine = (idx) => { if (entryForm.lines.length <= 2) return; setEntryForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) })); };

  const totalDr = entryForm.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCr = entryForm.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

  const handleCreateEntry = async () => {
    if (!isBalanced) { toast.error('Entry must be balanced'); return; }
    setSaving(true);
    try {
      const payload = {
        ...entryForm,
        lines: entryForm.lines.map(l => ({
          account_code: l.account_code || selectedAccount,
          debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0, narration: l.narration
        }))
      };
      const res = await accountingAPI.createJournalEntry(payload);
      toast.success(`Voucher ${res.data.voucher_number} created`);
      setEntryDialog(false);
      setEntryForm({ date: new Date().toISOString().split('T')[0], voucher_type: 'journal', narration: '', party_name: '', lines: [{ ...emptyLine }, { ...emptyLine }] });
      loadLedger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // Edit voucher
  const openEdit = async (entryId) => {
    try {
      const res = await accountingAPI.getJournalEntry(entryId);
      const e = res.data;
      setEditForm({
        date: e.date, voucher_type: e.voucher_type, narration: e.narration, party_name: e.party_name || '',
        lines: e.lines.map(l => ({ account_code: l.account_code, debit: l.debit || '', credit: l.credit || '', narration: l.narration || '' }))
      });
      setEditDialog({ open: true, entry: e });
    } catch { toast.error('Failed to load entry'); }
  };

  const handleSaveEdit = async () => {
    if (!editForm || !editDialog.entry) return;
    const dr = editForm.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const cr = editForm.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    if (Math.abs(dr - cr) > 0.01) { toast.error('Entry must be balanced'); return; }
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        lines: editForm.lines.map(l => ({ account_code: l.account_code, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0, narration: l.narration }))
      };
      await accountingAPI.editJournalEntry(editDialog.entry.entry_id, payload);
      toast.success('Voucher updated');
      setEditDialog({ open: false, entry: null });
      loadLedger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Edit failed'); }
    finally { setSaving(false); }
  };

  const handleExportPdf = () => {
    if (!selectedAccount) return;
    const token = ''; // Token is sent via interceptor; URL auth not needed for window.open
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams();
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    window.open(`${API_URL}/api/accounting/ledger/${selectedAccount}/pdf?${params.toString()}`, '_blank');
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const accountsByType = {};
  accounts.forEach(a => { const t = a.account_type; if (!accountsByType[t]) accountsByType[t] = []; accountsByType[t].push(a); });
  const typeLabels = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', revenue: 'Revenue', direct_expense: 'Direct Expenses', indirect_expense: 'Indirect Expenses' };

  return (
    <div data-testid="legacy-ledger-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Legacy Ledger</h1>
          <p className="text-sm text-slate-500 mt-0.5">Busy-style accounting ledger with running balance</p>
        </div>
        <div className="flex gap-2">
          {selectedAccount && (
            <>
              <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="ledger-export-pdf">
                <FileDown className="w-3.5 h-3.5 mr-1" /> Export PDF
              </Button>
              <Button size="sm" onClick={() => setEntryDialog(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="ledger-add-entry">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Entry
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Account Selector */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="p-2 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="form-input pl-8 text-xs py-1.5" data-testid="ledger-search" />
            </div>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {filtered.map(a => (
              <button key={a.account_code} onClick={() => { setSelectedAccount(a.account_code); setFilters(f => ({ ...f, page: 1 })); setExpanded({}); }}
                className={`w-full text-left px-2.5 py-2 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                  selectedAccount === a.account_code ? 'bg-orange-50 border-l-2 border-l-orange-500' : ''
                }`} data-testid={`ledger-acc-${a.account_code}`}>
                <p className="text-[10px] font-mono text-slate-400">{a.account_code}</p>
                <p className="text-xs text-slate-800 truncate">{a.account_name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Ledger View - Flexi Style */}
        <div className="lg:col-span-4">
          {!selectedAccount ? (
            <div className="bg-white border border-slate-200 rounded-md flex flex-col items-center justify-center py-20">
              <BookOpenCheck className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Select an account to view its ledger</p>
            </div>
          ) : loading ? (
            <div className="bg-white border border-slate-200 rounded-md flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : ledger ? (
            <div className="space-y-3">
              {/* Flexi-style Header */}
              <div className="bg-white border border-slate-200 rounded-md p-4 text-center" data-testid="ledger-header">
                <h2 className="font-bold text-sm text-slate-900 uppercase tracking-wide">{company?.name || 'Company'}</h2>
                {company?.address && <p className="text-[10px] text-slate-500 mt-0.5">{company.address}</p>}
                <div className="flex items-center justify-center gap-3 text-[9px] text-slate-400 mt-0.5">
                  {company?.cin && <span>CIN: {company.cin}</span>}
                  {company?.gstin && <span>GSTIN: {company.gstin}</span>}
                </div>
                <p className="font-bold text-xs text-slate-800 mt-2 tracking-wider">LEDGER</p>
                <p className="text-[10px] text-slate-600 mt-0.5">
                  Account: <strong>{ledger.account.account_name}</strong> ({ledger.account.account_code})
                  {' | '}Period: {filters.date_from || 'Start'} to {filters.date_to || 'Current'}
                </p>
              </div>

              {/* Date Filters */}
              <div className="flex gap-2 items-center">
                <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value, page: 1 }))}
                  className="form-input w-32 text-xs py-1.5" data-testid="ledger-date-from" placeholder="From" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value, page: 1 }))}
                  className="form-input w-32 text-xs py-1.5" data-testid="ledger-date-to" />
                <div className="ml-auto flex gap-3 text-xs">
                  <span className="text-blue-600 font-mono font-semibold">Dr: Rs. {formatCurrency(ledger.total_debit)}</span>
                  <span className="text-rose-600 font-mono font-semibold">Cr: Rs. {formatCurrency(ledger.total_credit)}</span>
                </div>
              </div>

              {/* Ledger Table */}
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full" data-testid="ledger-table">
                  <thead>
                    <tr className="bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wider">
                      <th className="px-2.5 py-2 text-left w-20">Date</th>
                      <th className="px-2.5 py-2 text-left w-14">Type</th>
                      <th className="px-2.5 py-2 text-left w-28">Vch No.</th>
                      <th className="px-2.5 py-2 text-left">Particulars</th>
                      <th className="px-2.5 py-2 text-left w-32">Narration</th>
                      <th className="px-2.5 py-2 text-right w-24">Debit (*)</th>
                      <th className="px-2.5 py-2 text-right w-24">Credit (*)</th>
                      <th className="px-2.5 py-2 text-right w-28">Balance (*)</th>
                      <th className="px-2.5 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.ledger.map((row, idx) => (
                      <React.Fragment key={idx}>
                        <tr className={`border-b border-slate-100 hover:bg-slate-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                          data-testid={`ledger-row-${idx}`}>
                          <td className="px-2.5 py-2 text-xs text-slate-600 font-mono">{row.date}</td>
                          <td className="px-2.5 py-2 text-[10px] capitalize text-slate-500">{row.voucher_type.replace('_', ' ').slice(0, 6)}</td>
                          <td className="px-2.5 py-2 text-[10px] font-mono text-slate-600 truncate max-w-[110px]">{row.voucher_number}</td>
                          <td className="px-2.5 py-2 text-xs text-slate-800">
                            <button onClick={() => toggleExpand(row.entry_id)} className="flex items-center gap-1 hover:text-orange-600">
                              {expanded[row.entry_id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              {row.narration || '-'}
                            </button>
                          </td>
                          <td className="px-2.5 py-2 text-[10px] text-slate-500 truncate max-w-[120px]">{row.narration}</td>
                          <td className="px-2.5 py-2 text-right font-mono text-xs">
                            {row.debit > 0 ? <span className="text-blue-700">{formatCurrency(row.debit)}</span> : ''}
                          </td>
                          <td className="px-2.5 py-2 text-right font-mono text-xs">
                            {row.credit > 0 ? <span className="text-rose-700">{formatCurrency(row.credit)}</span> : ''}
                          </td>
                          <td className="px-2.5 py-2 text-right font-mono text-xs font-semibold">
                            <span className={row.balance >= 0 ? 'text-slate-800' : 'text-rose-700'}>
                              {formatCurrency(Math.abs(row.balance))} {row.balance >= 0 ? 'Dr' : 'Cr'}
                            </span>
                          </td>
                          <td className="px-2.5 py-2">
                            <button onClick={() => openEdit(row.entry_id)} className="p-1 hover:bg-slate-200 rounded" data-testid={`ledger-edit-${idx}`}>
                              <Edit className="w-3 h-3 text-slate-400" />
                            </button>
                          </td>
                        </tr>
                        {/* Component lines (indented breakdown) */}
                        {expanded[row.entry_id] && (
                          <tr className="bg-amber-50/30">
                            <td colSpan={9} className="px-2.5 py-1.5">
                              <div className="ml-16 text-[10px] text-slate-500 space-y-0.5" data-testid={`ledger-detail-${idx}`}>
                                <p className="font-semibold text-slate-600">Voucher: {row.voucher_number} | Type: {row.voucher_type}</p>
                                <p className="italic">{row.narration}</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {ledger.ledger.length === 0 && (
                      <tr><td colSpan={9} className="text-center text-slate-400 py-12 text-sm">No transactions. Use "+ Add Entry" to create one.</td></tr>
                    )}
                  </tbody>
                  {/* Flexi-style Summary Footer */}
                  {ledger.ledger.length > 0 && (
                    <tfoot>
                      <tr className="bg-slate-100 font-bold text-xs border-t-2 border-slate-300">
                        <td colSpan={5} className="px-2.5 py-2 text-right">Total</td>
                        <td className="px-2.5 py-2 text-right font-mono text-blue-800">{formatCurrency(ledger.total_debit)}</td>
                        <td className="px-2.5 py-2 text-right font-mono text-rose-800">{formatCurrency(ledger.total_credit)}</td>
                        <td colSpan={2}></td>
                      </tr>
                      <tr className="bg-slate-100 font-bold text-xs">
                        <td colSpan={5} className="px-2.5 py-1.5 text-right">
                          {ledger.closing_balance >= 0 ? 'Debit Balance' : 'Credit Balance'}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono" colSpan={2}>
                          <span className={ledger.closing_balance >= 0 ? 'text-blue-800' : 'text-rose-800'}>
                            {formatCurrency(Math.abs(ledger.closing_balance))}
                          </span>
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                      <tr className="bg-slate-900 text-white font-bold text-xs">
                        <td colSpan={5} className="px-2.5 py-2 text-right">Grand Total</td>
                        <td className="px-2.5 py-2 text-right font-mono">{formatCurrency(Math.max(ledger.total_debit, ledger.total_credit))}</td>
                        <td className="px-2.5 py-2 text-right font-mono">{formatCurrency(Math.max(ledger.total_debit, ledger.total_credit))}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Add Entry Dialog */}
      <Dialog open={entryDialog} onOpenChange={setEntryDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Ledger Entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="form-label text-xs">Date</label>
                <input type="date" value={entryForm.date} onChange={(e) => setEntryForm(f => ({ ...f, date: e.target.value }))}
                  className="form-input text-sm" data-testid="le-date" />
              </div>
              <div>
                <label className="form-label text-xs">Voucher Type</label>
                <select value={entryForm.voucher_type} onChange={(e) => setEntryForm(f => ({ ...f, voucher_type: e.target.value }))}
                  className="form-input text-sm" data-testid="le-type">
                  {VOUCHER_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label text-xs">Party Name</label>
                <input type="text" value={entryForm.party_name} onChange={(e) => setEntryForm(f => ({ ...f, party_name: e.target.value }))}
                  className="form-input text-sm" data-testid="le-party" />
              </div>
            </div>
            <div>
              <label className="form-label text-xs">Narration</label>
              <input type="text" value={entryForm.narration} onChange={(e) => setEntryForm(f => ({ ...f, narration: e.target.value }))}
                className="form-input text-sm" data-testid="le-narration" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase">
                  <th className="px-2 py-1.5 text-left">#</th><th className="px-2 py-1.5 text-left">Account</th>
                  <th className="px-2 py-1.5 text-right w-28">Debit</th><th className="px-2 py-1.5 text-right w-28">Credit</th>
                  <th className="px-2 py-1.5 text-left w-32">Narration</th><th className="w-8"></th>
                </tr></thead>
                <tbody>
                  {entryForm.lines.map((line, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="px-2 py-1 text-xs text-slate-400">{idx + 1}</td>
                      <td className="px-2 py-1">
                        <select value={line.account_code} onChange={(e) => updateLine(idx, 'account_code', e.target.value)}
                          className="form-input text-xs" data-testid={`le-acc-${idx}`}>
                          <option value="">Select</option>
                          {Object.entries(accountsByType).map(([type, accs]) => (
                            <optgroup key={type} label={typeLabels[type] || type}>
                              {accs.map(a => <option key={a.account_code} value={a.account_code}>{a.account_code} - {a.account_name}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1"><input type="number" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)} className="form-input text-xs text-right font-mono" placeholder="0.00" data-testid={`le-dr-${idx}`} /></td>
                      <td className="px-2 py-1"><input type="number" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)} className="form-input text-xs text-right font-mono" placeholder="0.00" data-testid={`le-cr-${idx}`} /></td>
                      <td className="px-2 py-1"><input type="text" value={line.narration} onChange={(e) => updateLine(idx, 'narration', e.target.value)} className="form-input text-xs" /></td>
                      <td className="px-2 py-1"><button onClick={() => removeLine(idx)} className="p-1 hover:bg-rose-50 rounded"><Trash2 className="w-3 h-3 text-rose-400" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button variant="outline" size="sm" onClick={addLine} className="text-xs mt-2"><Plus className="w-3 h-3 mr-1" /> Add Line</Button>
            </div>
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className={isBalanced ? 'text-emerald-600' : 'text-rose-600'}>{isBalanced ? 'Balanced' : `Diff: Rs. ${formatCurrency(Math.abs(totalDr - totalCr))}`}</span>
              <span className="font-mono">Dr: {formatCurrency(totalDr)} | Cr: {formatCurrency(totalCr)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateEntry} disabled={saving || !isBalanced} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="le-save">
              {saving ? 'Saving...' : 'Post Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Voucher Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, entry: null })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Voucher: {editDialog.entry?.voucher_number}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="form-label text-xs">Date</label>
                  <input type="date" value={editForm.date} onChange={(e) => setEditForm(f => ({ ...f, date: e.target.value }))} className="form-input text-sm" data-testid="edit-date" />
                </div>
                <div>
                  <label className="form-label text-xs">Type</label>
                  <select value={editForm.voucher_type} onChange={(e) => setEditForm(f => ({ ...f, voucher_type: e.target.value }))} className="form-input text-sm" data-testid="edit-type">
                    {VOUCHER_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label text-xs">Party</label>
                  <input type="text" value={editForm.party_name} onChange={(e) => setEditForm(f => ({ ...f, party_name: e.target.value }))} className="form-input text-sm" data-testid="edit-party" />
                </div>
              </div>
              <div>
                <label className="form-label text-xs">Narration</label>
                <input type="text" value={editForm.narration} onChange={(e) => setEditForm(f => ({ ...f, narration: e.target.value }))} className="form-input text-sm" data-testid="edit-narration" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase">
                    <th className="px-2 py-1.5 text-left">#</th><th className="px-2 py-1.5 text-left">Account</th>
                    <th className="px-2 py-1.5 text-right w-28">Debit</th><th className="px-2 py-1.5 text-right w-28">Credit</th>
                    <th className="px-2 py-1.5 text-left w-32">Narration</th><th className="w-8"></th>
                  </tr></thead>
                  <tbody>
                    {editForm.lines.map((line, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-2 py-1 text-xs text-slate-400">{idx + 1}</td>
                        <td className="px-2 py-1">
                          <select value={line.account_code} onChange={(e) => setEditForm(f => { const lines = [...f.lines]; lines[idx] = { ...lines[idx], account_code: e.target.value }; return { ...f, lines }; })}
                            className="form-input text-xs" data-testid={`edit-acc-${idx}`}>
                            <option value="">Select</option>
                            {Object.entries(accountsByType).map(([type, accs]) => (
                              <optgroup key={type} label={typeLabels[type] || type}>
                                {accs.map(a => <option key={a.account_code} value={a.account_code}>{a.account_code} - {a.account_name}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1"><input type="number" value={line.debit} onChange={(e) => setEditForm(f => { const lines = [...f.lines]; lines[idx] = { ...lines[idx], debit: e.target.value }; return { ...f, lines }; })} className="form-input text-xs text-right font-mono" data-testid={`edit-dr-${idx}`} /></td>
                        <td className="px-2 py-1"><input type="number" value={line.credit} onChange={(e) => setEditForm(f => { const lines = [...f.lines]; lines[idx] = { ...lines[idx], credit: e.target.value }; return { ...f, lines }; })} className="form-input text-xs text-right font-mono" data-testid={`edit-cr-${idx}`} /></td>
                        <td className="px-2 py-1"><input type="text" value={line.narration} onChange={(e) => setEditForm(f => { const lines = [...f.lines]; lines[idx] = { ...lines[idx], narration: e.target.value }; return { ...f, lines }; })} className="form-input text-xs" /></td>
                        <td className="px-2 py-1">
                          {editForm.lines.length > 2 && (
                            <button onClick={() => setEditForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))} className="p-1 hover:bg-rose-50 rounded"><Trash2 className="w-3 h-3 text-rose-400" /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Button variant="outline" size="sm" onClick={() => setEditForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }))} className="text-xs mt-2"><Plus className="w-3 h-3 mr-1" /> Add Line</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, entry: null })}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="edit-save">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
